import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, ArrowUpRight, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { CATEGORIES, CONDITIONS, imgSrc } from "@/lib/constants";
import { Reveal, SectionKicker } from "./Reveal";

export const conditionStyle = {
  neu: "border border-white/70 bg-white/5 text-white -rotate-1",
  wie_neu: "border border-[#93A0B8]/60 bg-[#93A0B8]/5 text-[#B9C4D6]",
  vinted: "border border-[#C07A63]/70 bg-[#C07A63]/5 text-[#C07A63] rotate-1",
};

const ProductCard = ({ product, onOpen }) => {
  const { t } = useLang();
  const status = product.status || (product.is_available ? "available" : "sold");
  const sold = status === "sold";
  const reserved = status === "reserved";
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -4 }}
      data-testid={`product-card-${product.code}`}
      onClick={() => onOpen(product)}
      className="group cursor-pointer border border-edge bg-surface transition-all duration-300 hover:border-edge-bright hover:shadow-[0_8px_28px_rgba(0,0,0,0.35)]"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#101B30] p-2">
        <div className="relative h-full w-full overflow-hidden border border-edge/70">
          <img
            src={imgSrc(product.image_url)}
            alt={product.title}
            loading="lazy"
            className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${sold ? "opacity-40 grayscale" : ""}`}
          />
          <div className="absolute left-3 top-3 bg-[#0B1526]/85 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-mutedink backdrop-blur-sm">
            [{product.code}]
          </div>
          <div className={`absolute right-3 top-3 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest backdrop-blur-sm ${conditionStyle[product.condition] || conditionStyle.vinted}`}>
            {t.conditions[product.condition] || product.condition}
          </div>
          {sold && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="rotate-[-8deg] border-2 border-signal bg-[#0B1526]/85 px-4 py-1.5 font-mono text-sm font-bold uppercase tracking-widest text-signal" data-testid={`sold-badge-${product.code}`}>
                {t.product.sold}
              </span>
            </div>
          )}
          {reserved && (
            <div className="absolute bottom-3 left-3 border border-reservedamber bg-[#0B1526]/85 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-reservedamber backdrop-blur-sm" data-testid={`reserved-badge-${product.code}`}>
              {t.product.reserved}
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-edge p-4">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-volt">{product.brand}</span>
          <span className="font-mono text-[9px] uppercase tracking-widest text-mutedink/70">{t.product.unique}</span>
        </div>
        <h3 className="mt-1.5 font-grotesk text-base font-semibold tracking-tight text-ink sm:text-lg">{product.title}</h3>
        <div className="mt-3 flex items-center justify-between">
          <span className="font-mono text-xs text-mutedink">{t.product.size} {product.size}</span>
          <span className="font-mono text-base font-bold text-ink" data-testid={`price-${product.code}`}>€{product.price}</span>
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-edge/70 pt-3">
          <span className="font-grotesk text-xs uppercase tracking-widest text-mutedink transition-colors group-hover:text-volt">
            {t.product.details}
          </span>
          <span className="flex h-7 w-7 items-center justify-center border border-edge text-mutedink transition-colors duration-300 group-hover:border-volt group-hover:text-volt">
            <ArrowUpRight size={13} />
          </span>
        </div>
      </div>
    </motion.article>
  );
};

export const ProductCatalog = ({ onOpen, refreshKey = 0 }) => {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("all");
  const [condition, setCondition] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api.get("/products").then((res) => setProducts(res.data)).catch(() => {});
  }, [refreshKey]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (category !== "all") list = list.filter((p) => p.category === category);
    if (condition !== "all") list = list.filter((p) => p.condition === condition);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((p) => `${p.title} ${p.brand}`.toLowerCase().includes(q));
    }
    if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, category, condition, query, sort]);

  const reset = () => {
    setCategory("all");
    setCondition("all");
    setQuery("");
    setSort("featured");
  };

  return (
    <section id="katalog" data-testid="catalog-section" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 md:px-8 md:py-32">
      <Reveal>
        <SectionKicker>{t.catalog.kicker}</SectionKicker>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl" data-testid="catalog-title">
            {t.catalog.title}
          </h2>
          <span className="font-mono text-xs uppercase tracking-widest text-mutedink" data-testid="catalog-count">
            {filtered.length} {t.catalog.count}
          </span>
        </div>
      </Reveal>

      <Reveal delay={0.1} className="mt-10">
        <div className="flex flex-col gap-4 border border-edge bg-surface p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2" data-testid="category-filters">
            {["all", ...CATEGORIES].map((c) => (
              <button
                key={c}
                data-testid={`filter-category-${c === "all" ? "all" : c.toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={`border px-3.5 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors duration-300 ${
                  category === c ? "border-volt bg-volt text-obsidian" : "border-edge text-mutedink hover:border-edge-bright hover:text-ink"
                }`}
              >
                {c === "all" ? t.catalog.all : c}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2" data-testid="condition-filters">
              {["all", ...CONDITIONS].map((c) => (
                <button
                  key={c}
                  data-testid={`filter-condition-${c}`}
                  onClick={() => setCondition(c)}
                  className={`px-3 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors duration-300 ${
                    condition === c ? "bg-ink text-obsidian" : "text-mutedink hover:text-ink"
                  }`}
                >
                  {c === "all" ? t.catalog.all : t.conditions[c]}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mutedink" />
              <input
                data-testid="catalog-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.catalog.search}
                className="w-56 border border-edge bg-obsidian py-2 pl-9 pr-3 font-grotesk text-sm text-ink placeholder:text-mutedink/60 focus:border-volt focus:outline-none"
              />
            </div>
            <div className="relative">
              <select
                data-testid="catalog-sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="appearance-none border border-edge bg-obsidian py-2 pl-3 pr-8 font-mono text-[11px] uppercase tracking-widest text-ink focus:border-volt focus:outline-none"
              >
                <option value="featured">{t.catalog.sort.featured}</option>
                <option value="price_asc">{t.catalog.sort.price_asc}</option>
                <option value="price_desc">{t.catalog.sort.price_desc}</option>
              </select>
              <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-mutedink" />
            </div>
          </div>
        </div>
      </Reveal>

      {filtered.length === 0 ? (
        <div className="mt-16 flex flex-col items-center gap-4 border border-dashed border-edge-bright py-20" data-testid="catalog-empty">
          <p className="font-grotesk text-mutedink">{t.catalog.empty}</p>
          <button data-testid="catalog-reset-button" onClick={reset} className="border border-volt px-5 py-2.5 font-mono text-xs uppercase tracking-widest text-volt transition-colors hover:bg-volt hover:text-obsidian">
            {t.catalog.reset}
          </button>
        </div>
      ) : (
        <motion.div layout className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3 xl:grid-cols-4" data-testid="catalog-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <ProductCard key={p.id || p.code} product={p} onOpen={onOpen} />
            ))}
          </AnimatePresence>
        </motion.div>
      )}
    </section>
  );
};
